'use client'

import { cn, Popover, PopoverContent, PopoverTrigger } from 'design-system'
import { ListFilter } from 'lucide-react'
import { FC, useEffect, useRef, useState } from 'react'

import { MainMenu } from './main-menu'
import { SubFilterView } from './sub-filter-view'
import { FilterConfig, FilterDropdownProps } from './types'

const TRIGGER_BASE =
  'flex cursor-pointer items-center gap-2 rounded-lg font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 aria-expanded:bg-slate-100 aria-expanded:text-slate-900'

const TRIGGER_SIZES = {
  sm: 'px-2 py-1 text-xs',
  base: 'px-3 py-1.5 text-sm'
} as const

export const FilterDropdown: FC<FilterDropdownProps> = ({
  filters,
  values,
  onChange,
  triggerProps
}) => {
  const [open, setOpen] = useState(false)
  // Track the selected filter by key and resolve it from the live `filters`
  // prop, so the open sub-filter sees updated items/loading (e.g. server-side
  // search results) instead of a snapshot taken when it was clicked.
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const selectedFilter = filters.find(f => f.key === selectedKey) ?? null
  const contentRef = useRef<HTMLDivElement>(null)

  const handleToggle = () => setOpen(prev => !prev)

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next) setSelectedKey(null)
  }

  // Move focus to the first interactive element when the view swaps.
  // When a sub-filter is selected, skip the header (Back button) and focus the
  // first interactive element inside the filter content area instead.
  useEffect(() => {
    if (!open) return
    const node = contentRef.current
    if (!node) return
    const scope = selectedKey
      ? node.querySelector<HTMLElement>('section > :not(header)')
      : node
    const focusable = (scope ?? node).querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
    focusable?.focus()
  }, [selectedKey, open])

  const size = triggerProps?.size ?? 'base'
  const iconSize = size === 'sm' ? 14 : 18

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            TRIGGER_BASE,
            TRIGGER_SIZES[size],
            triggerProps?.className
          )}
          aria-expanded={open}
          aria-haspopup="dialog"
          onClick={handleToggle}>
          <ListFilter size={iconSize} aria-hidden />
          Filter
        </button>
      </PopoverTrigger>
      {/* z-10 lifts the menu above sticky table header cells (z-5), which
          otherwise win the paint-order tie and cover the dropdown. */}
      <PopoverContent className="z-10 w-64" aria-label="Filter options">
        <div ref={contentRef}>
          {selectedFilter ? (
            <SubFilterView
              filter={selectedFilter}
              values={values}
              onChange={onChange}
              onBack={() => setSelectedKey(null)}
            />
          ) : (
            <MainMenu
              filters={filters}
              onSelect={(filter: FilterConfig) => setSelectedKey(filter.key)}
            />
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
